/**
 * Surfaces the server-run first search in the browser console, since SSR keeps
 * it out of the Network tab. Headless spreads `requestExecuted` into the
 * fulfilled payload at runtime, but `ExecuteSearchThunkReturn` doesn't declare
 * it, so the payload is read untyped. The request carries the shared anonymous
 * `accessToken`, which is stripped so console dumps can be pasted anywhere.
 */
import type { CoveoSearchHub } from '@lib/coveo/coveoConstants';

type InitialSearchPayload = {
  requestExecuted?: Record<string, unknown> & { accessToken?: unknown };
  response?: unknown;
  duration?: number;
  queryExecuted?: string;
};

export type InitialSearchLog = {
  request: Record<string, unknown>;
  response: unknown;
  duration: number | undefined;
  queryExecuted: string | undefined;
};

const isObject = (value: unknown): value is object =>
  typeof value === 'object' && value !== null;

export function toInitialSearchLog(
  searchAction: unknown
): InitialSearchLog | null {
  if (!isObject(searchAction)) {
    return null;
  }
  const { payload } = searchAction as { payload?: unknown };
  if (!isObject(payload)) {
    return null;
  }
  const { requestExecuted, response, duration, queryExecuted } =
    payload as InitialSearchPayload;
  if (!requestExecuted && !response) {
    return null;
  }
  const { accessToken: _accessToken, ...request } = requestExecuted ?? {};
  return { request, response, duration, queryExecuted };
}

export function logInitialSearch(searchAction: unknown, hub: CoveoSearchHub) {
  const log = toInitialSearchLog(searchAction);
  if (!log) {
    return;
  }
  console.debug(`[Coveo SSR] initial search (${hub})`, log);
}
